/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { Fragment, jsx } from '@amazon/vinyl-tsx'
import { AsyncComponentLoader } from '@/components/AsyncComponentLoader'
import { featureEnabled, type FeatureFlag } from '@/core/featureGate'

export interface FeatureGateProps {
    readonly flag: FeatureFlag
    /**
     * Built and mounted only once the gate resolves to the wrapper's polarity.
     * A factory (not an element) so the gated component isn't constructed when
     * the gate doesn't match.
     */
    readonly create: () => HTMLElement
}

// Mount `create()` only when the resolved gate matches `showWhenEnabled`. While
// the gate is loading nothing renders, so a gated surface never flashes the
// wrong state before the value settles. Built on AsyncComponentLoader, which
// owns the loading/error window; a non-matching gate yields an empty fragment.
function featureGated(flag: FeatureFlag, showWhenEnabled: boolean, create: () => HTMLElement) {
    return AsyncComponentLoader({
        loadData: () => featureEnabled(flag),
        createComponent: (enabled) => (enabled === showWhenEnabled ? create() : <></>),
    })
}

/** Renders `create()` only when `flag` resolves ENABLED (nothing while loading). */
export function FeatureGateEnabled(props: FeatureGateProps) {
    return featureGated(props.flag, true, props.create)
}

/** Renders `create()` only when `flag` resolves DISABLED (nothing while loading). */
export function FeatureGateDisabled(props: FeatureGateProps) {
    return featureGated(props.flag, false, props.create)
}
